import React, { useState, useEffect } from 'react';
import { Calendar, Search, PlusCircle, Pencil, Trash2, Clock, User, Stethoscope, Briefcase } from 'lucide-react';

// Nombre de la clave que usaremos en el localStorage para citas
const STORAGE_KEY = 'adminCitasData_V2'; // Cambié la clave para evitar conflictos con datos viejos

// Opciones de Estatus disponibles
const ESTATUS_OPTIONS = ['Programada', 'Confirmada', 'Cancelada', 'Finalizada', 'Pendiente'];

// Estructura de datos inicial si no hay nada en localStorage
const initialCitasData = [
    // La estructura de los datos iniciales ahora incluye IDs y nombres separados
    { id: 1, pacienteId: 'P001', medicoId: 'M001', estatus: 'Programada', fecha: '2025-11-20', hora: '10:00', pacienteNombre: 'Ana García', medicoNombre: 'Dr. Lisset', consultorio: 'Sala 1A', motivo: 'Chequeo anual y análisis pendientes.' },
    { id: 2, pacienteId: 'P002', medicoId: 'M002', estatus: 'Finalizada', fecha: '2025-11-21', hora: '15:30', pacienteNombre: 'Roberto Pérez', medicoNombre: 'Dr. Javier', consultorio: 'Sala 2B', motivo: 'Consulta pediátrica por fiebre alta y tos.' },
    { id: 3, pacienteId: 'P003', medicoId: 'M001', estatus: 'Pendiente', fecha: '2025-11-22', hora: '09:00', pacienteNombre: 'María López', medicoNombre: 'Dr. Lisset', consultorio: 'Teleconsulta', motivo: 'Revisión dental y limpieza. Reprogramación.' },
];

// Estructura inicial para el formulario de una cita
const initialCita = {
    id: null, 
    // CLAVES RELACIONALES (Importantes para el futuro)
    pacienteId: '', 
    medicoId: '',
    // CAMPOS DE REFERENCIA
    pacienteNombre: '',
    medicoNombre: '',
    consultorio: '',
    // CAMPOS DE CITA
    estatus: ESTATUS_OPTIONS[0], 
    fecha: new Date().toISOString().substring(0, 10), // Fecha actual por defecto
    hora: '09:00', 
    motivo: ''
};

// Función de utilidad para obtener colores de estatus
const getStatusColor = (estatus) => {
    switch (estatus) {
        case 'Programada':
            return 'bg-green-100 text-green-800 ring-green-500';
        case 'Finalizada':
            return 'bg-blue-100 text-blue-800 ring-blue-500';
        case 'Pendiente':
            return 'bg-yellow-100 text-yellow-800 ring-yellow-500';
        case 'Cancelada':
            return 'bg-red-100 text-red-800 ring-red-500';
        case 'Confirmada':
            return 'bg-indigo-100 text-indigo-800 ring-indigo-500';
        default:
            return 'bg-gray-100 text-gray-800 ring-gray-500';
    }
};

export default function CitasView() {
    // 1. Cargar datos del localStorage o usar los iniciales
    const [citas, setCitas] = useState(() => {
        try {
            const storedData = localStorage.getItem(STORAGE_KEY);
            return storedData ? JSON.parse(storedData) : initialCitasData;
        } catch (error) {
            console.error("Error cargando datos de localStorage para citas:", error);
            return initialCitasData;
        }
    });

    // 2. Usar useEffect para guardar los datos cada vez que 'citas' cambie
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(citas));
        } catch (error) {
            console.error("Error guardando datos de citas en localStorage:", error);
        }
    }, [citas]); 

    // --- ESTADO PARA LA BÚSQUEDA ---
    const [searchTerm, setSearchTerm] = useState('');

    // Estados para la Interfaz
    const [message, setMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [currentCita, setCurrentCita] = useState(initialCita);
    const [isEditing, setIsEditing] = useState(false);

    // --- LÓGICA DE FILTRADO ---
    const filteredCitas = citas
        .filter(cita => {
            const lowerCaseSearch = searchTerm.toLowerCase();
            
            if (!lowerCaseSearch) return true;

            // Búsqueda en todos los campos relevantes, incluidos los nuevos IDs
            return (
                cita.pacienteNombre?.toLowerCase().includes(lowerCaseSearch) ||
                cita.medicoNombre?.toLowerCase().includes(lowerCaseSearch) ||
                cita.motivo?.toLowerCase().includes(lowerCaseSearch) ||
                cita.pacienteId?.toLowerCase().includes(lowerCaseSearch) || 
                cita.medicoId?.toLowerCase().includes(lowerCaseSearch) || 
                cita.estatus?.toLowerCase().includes(lowerCaseSearch) ||    
                cita.fecha?.includes(lowerCaseSearch) || 
                cita.hora?.includes(lowerCaseSearch)
            );
        })
        // Opcional: Ordenar por fecha y hora (más reciente primero)
        .sort((a, b) => {
            const dateA = new Date(`${a.fecha}T${a.hora}`);
            const dateB = new Date(`${b.fecha}T${b.hora}`);
            return dateA - dateB; // Citas más antiguas primero
        });

    // --- Funciones de Utilidad y Notificación ---
    const showMessage = (msg) => {
        setMessage(msg);
        setTimeout(() => setMessage(''), 3000);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsEditing(false);
        setCurrentCita(initialCita);
    };

    // CREAR/EDITAR
    const handleCreate = () => {
        setIsEditing(false);
        setCurrentCita({
            ...initialCita, 
            estatus: ESTATUS_OPTIONS[0], 
            fecha: new Date().toISOString().substring(0, 10), // Usar fecha actual
        }); 
        setIsModalOpen(true);
    };

    const handleEdit = (cita) => {
        setIsEditing(true);
        setCurrentCita(cita); 
        setIsModalOpen(true);
    };

    // ELIMINAR (Abre la modal de confirmación)
    const handleDelete = (cita) => {
        setCurrentCita(cita);
        setIsDeleteModalOpen(true);
    };

    // Confirmación de ELIMINAR
    const confirmDelete = () => {
        setCitas(citas.filter(c => c.id !== currentCita.id));
        showMessage(`[ELIMINADO] Cita de ${currentCita.pacienteNombre} eliminada permanentemente.`);
        setIsDeleteModalOpen(false);
        setCurrentCita(initialCita);
    };

    // Manejar cambios en el formulario
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setCurrentCita({ ...currentCita, [name]: value });
    };

    // Guardar el formulario (CREATE o UPDATE)
    const handleSubmit = (e) => {
        e.preventDefault();
        
        // **Validaciones actualizadas para los IDs relacionales**
        if (!currentCita.pacienteId || !currentCita.medicoId || !currentCita.pacienteNombre || !currentCita.estatus) {
             showMessage(`[ERROR] ID de Paciente, ID de Médico, Nombre del Paciente y Estatus son obligatorios.`);
             return;
        }

        if (isEditing) {
            // Lógica de Edición (UPDATE)
            setCitas(citas.map(c => c.id === currentCita.id 
                ? currentCita 
                : c)
            );
            showMessage(`[ACTUALIZADA] Cita de ${currentCita.pacienteNombre} modificada con éxito.`);
        } else {
            // Lógica de Creación (CREATE)
            // Asegura un ID único localmente
            const newId = citas.length > 0 ? Math.max(...citas.map(c => c.id)) + 1 : 1;
            
            setCitas([...citas, { ...currentCita, id: newId }]);
            showMessage(`[CREADA] Cita para ${currentCita.pacienteNombre} agendada con éxito.`);
        }

        closeModal();
    };


    return (
        <div className="p-4 sm:p-8 max-w-7xl mx-auto font-sans min-h-screen">

            {/* Notificación Visual Flotante */}
            {message && (
                <div className="fixed top-0 left-1/2 transform -translate-x-1/2 mt-4 z-50">
                    <div className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg shadow-xl animate-pulse transition duration-500 flex items-center">
                        <Calendar className="w-5 h-5 mr-2" />
                        {message}
                    </div>
                </div>
            )}
            
            <header className="flex items-center mb-6 border-b pb-3">
                <Calendar className="w-7 h-7 mr-3 text-indigo-600" />
                <h1 className="text-3xl font-extrabold text-gray-900">Gestión de Citas Médicas</h1>
            </header>

            {/* Controles: Botón Crear y Barra de Búsqueda */}
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0 sm:space-x-4">
                {/* Botón para crear */}
                <button
                    onClick={handleCreate} 
                    className="flex items-center w-full sm:w-auto justify-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow-md hover:bg-indigo-700 transition duration-150 transform hover:scale-[1.01]"
                >
                    <PlusCircle className="w-5 h-5 mr-2" />
                    Agendar Nueva Cita
                </button>
                
                {/* BARRA DE BÚSQUEDA */}
                <div className="relative w-full sm:w-1/2 lg:w-1/3">
                    <input
                        type="text"
                        placeholder="Buscar por paciente, ID, estatus, médico..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {/* Icono de búsqueda */}
                    <Search className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 w-5 h-5 mt-2" />
                </div>
            </div>

            <h2 className="text-xl font-semibold text-gray-700 mb-4">
                Citas Programadas ({filteredCitas.length})
                {searchTerm && <span className="text-sm font-normal text-gray-500 ml-2"> (Filtro activo)</span>}
            </h2>

            {/* Tabla de Citas */}
            <div className="overflow-x-auto bg-white shadow-lg rounded-xl">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Local</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paciente (ID)</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha / Hora</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Médico (ID)</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estatus</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredCitas.length > 0 ? (
                            filteredCitas.map((cita) => (
                                <tr key={cita.id} className="hover:bg-indigo-50 transition duration-150">
                                    <td className="px-3 py-4 text-sm font-medium text-indigo-600">{cita.id}</td>
                                    <td className="px-3 py-4 text-sm text-gray-900 font-semibold">
                                        {cita.pacienteNombre}
                                        <div className="text-xs text-gray-500 font-normal mt-0.5">ID: {cita.pacienteId}</div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-700">
                                        <div className="font-medium flex items-center"><Calendar className="w-4 h-4 mr-1 text-gray-500" />{cita.fecha}</div>
                                        <div className="text-xs text-gray-500 flex items-center"><Clock className="w-4 h-4 mr-1 text-gray-500" />{cita.hora}</div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-500 hidden sm:table-cell">
                                        <div className="flex items-center">
                                            <Stethoscope className="w-4 h-4 mr-1" />
                                            {cita.medicoNombre}
                                        </div>
                                        <div className="text-xs text-gray-400 font-normal mt-0.5">ID: {cita.medicoId}</div>
                                    </td>
                                    <td className="px-4 py-4">
                                        {/* Display de Estatus con color */}
                                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ring-1 ring-inset ${getStatusColor(cita.estatus)}`}>
                                            {cita.estatus}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end space-x-2">
                                            <button
                                                onClick={() => handleEdit(cita)} 
                                                className="text-indigo-600 hover:text-indigo-900 p-2 rounded-full hover:bg-indigo-100 transition duration-150"
                                                title="Editar Cita"
                                            >
                                                <Pencil className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(cita)} 
                                                className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100 transition duration-150"
                                                title="Eliminar Cita"
                                            >
                                                <Trash2 className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500 text-lg italic">
                                    No se encontraron citas que coincidan con la búsqueda.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* MODAL de CREAR/EDITAR */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100 border-t-4 border-indigo-600">
                        <h3 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                            <Calendar className="w-6 h-6 mr-2 text-indigo-600" />
                            {isEditing ? 'Editar Cita Existente' : 'Agendar Nueva Cita'}
                        </h3>
                        
                        <form onSubmit={handleSubmit}>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {/* Campo Fecha */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-medium mb-2">Fecha:</label>
                                    <input type="date" name="fecha" required value={currentCita.fecha} onChange={handleFormChange}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>

                                {/* Campo Hora */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-medium mb-2">Hora:</label>
                                    <input type="time" name="hora" required value={currentCita.hora} onChange={handleFormChange}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                
                                {/* Campo Estatus */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-medium mb-2">Estatus:</label>
                                    <select name="estatus" required value={currentCita.estatus} onChange={handleFormChange}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        {ESTATUS_OPTIONS.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border p-3 rounded-lg bg-gray-50 mb-4">
                                <h4 className="col-span-1 sm:col-span-2 text-md font-semibold text-indigo-700 border-b pb-2 mb-2">Datos de Vínculo Relacional</h4>
                                
                                {/* Campo Paciente NOMBRE (Referencia Visual) */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center"><User className="w-4 h-4 mr-1"/> Nombre Paciente (Referencia):</label>
                                    <input type="text" name="pacienteNombre" required value={currentCita.pacienteNombre} onChange={handleFormChange}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Nombre completo del paciente"
                                    />
                                </div>

                                {/* Campo ID Paciente (Clave) */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center">ID Paciente (Clave):</label>
                                    <input type="text" name="pacienteId" required value={currentCita.pacienteId} onChange={handleFormChange}
                                        className="shadow appearance-none border-2 border-indigo-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Ej: PAT-90123"
                                    />
                                </div>
                                
                                {/* Campo Médico NOMBRE (Referencia Visual) */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center"><Stethoscope className="w-4 h-4 mr-1"/> Nombre Médico (Referencia):</label>
                                    <input type="text" name="medicoNombre" required value={currentCita.medicoNombre} onChange={handleFormChange}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Nombre del Médico"
                                    />
                                </div>
                                
                                {/* Campo ID Médico (Clave) */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center">ID Médico (Clave):</label>
                                    <input type="text" name="medicoId" required value={currentCita.medicoId} onChange={handleFormChange}
                                        className="shadow appearance-none border-2 border-indigo-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="Ej: MED-456"
                                    />
                                </div>
                            </div>

                            {/* Campo Consultorio */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-medium mb-2 flex items-center"><Briefcase className="w-4 h-4 mr-1"/> Consultorio/Ubicación:</label>
                                <input type="text" name="consultorio" value={currentCita.consultorio} onChange={handleFormChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Ej: Sala 3A / Teleconsulta"
                                />
                            </div>

                            {/* Campo Motivo */}
                            <div className="mb-6">
                                <label className="block text-gray-700 text-sm font-medium mb-2">Motivo de la Cita (Detalles):</label>
                                <textarea name="motivo" required rows="3" value={currentCita.motivo} onChange={handleFormChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Descripción breve de la consulta, historial relevante, etc."
                                />
                            </div>

                            {/* Botones de acción */}
                            <div className="flex justify-end space-x-4">
                                <button type="button" onClick={closeModal}
                                    className="bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-400 transition duration-150">
                                    Cancelar
                                </button>
                                <button type="submit"
                                    className="bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-150 transform hover:scale-105 shadow-md">
                                    {isEditing ? 'Guardar Cambios' : 'Agendar Cita'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL de CONFIRMACIÓN de ELIMINACIÓN */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 border-t-4 border-red-600">
                        <h3 className="text-xl font-bold mb-4 text-red-600 flex items-center"><Trash2 className="w-5 h-5 mr-2" /> Confirmar Eliminación</h3>
                        <p className="text-gray-700 mb-6">
                            ¿Estás seguro de que quieres eliminar la cita para **{currentCita.pacienteNombre}** (ID Relacional: {currentCita.pacienteId}) el **{currentCita.fecha}**? Esta acción es permanente.
                        </p>
                        
                        <div className="flex justify-end space-x-4">
                            <button type="button" onClick={() => setIsDeleteModalOpen(false)}
                                className="bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-400 transition duration-150"
                            >
                                Cancelar
                            </button>
                            <button type="button" onClick={confirmDelete}
                                className="bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition duration-150 transform hover:scale-105 shadow-md"
                            >
                                Sí, Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}